import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const ReceiptUpload = ({
  orderId,
  onUploadSuccess,
  required = false,
  maxSize = 10 * 1024 * 1024, // 10MB default
  error
}) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const allowedTypes = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'application/pdf': '.pdf'
  };

  const validateFile = (file) => {
    // Check file type
    if (!allowedTypes[file.type]) {
      return 'Tipo de archivo no permitido. Solo se aceptan: JPG, PNG, GIF, WebP, PDF';
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return `El archivo es demasiado grande. Máximo ${maxSizeMB}MB permitido`;
    }

    return null;
  };

  const handleFileSelect = (file) => {
    setUploadError('');

    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !orderId) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('receipt', selectedFile);

      const xhr = new XMLHttpRequest();

      // Handle upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress(progress);
        }
      });

      // Handle response
      xhr.addEventListener('load', () => {
        if (xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          if (response.success) {
            setUploadProgress(100);
            if (onUploadSuccess) {
              onUploadSuccess(response.receipt);
            }
          } else {
            setUploadError(response.message || 'Error al subir archivo');
          }
        } else {
          const errorResponse = JSON.parse(xhr.responseText);
          setUploadError(errorResponse.message || 'Error al subir archivo');
        }
        setUploading(false);
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        setUploadError('Error de conexión al subir archivo');
        setUploading(false);
      });

      xhr.open('POST', `${process.env.REACT_APP_API_URL}/receipts/${orderId}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Error al subir archivo');
      setUploading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadProgress(0);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h3 style={{ marginBottom: '1rem', color: '#374151' }}>
        📎 Comprobante de Transferencia {required && <span style={{ color: '#dc2626' }}>*</span>}
      </h3>

      {(uploadError || error) && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          fontSize: '0.875rem'
        }}>
          {uploadError || error}
        </div>
      )}

      {!selectedFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragOver ? '#2563eb' : '#d1d5db'}`,
            borderRadius: '8px',
            padding: '3rem 2rem',
            textAlign: 'center',
            backgroundColor: isDragOver ? '#eff6ff' : '#fafafa',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
            Subir comprobante de transferencia
          </h4>
          <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
            Arrastra el archivo aquí o haz clic para seleccionar
          </p>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            Formatos: JPG, PNG, GIF, WebP, PDF<br />
            Tamaño máximo: {Math.round(maxSize / (1024 * 1024))}MB
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        <div style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '1.5rem',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            {/* Preview */}
            <div style={{ flexShrink: 0 }}>
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}
                />
              ) : (
                <div style={{
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '2rem'
                }}>
                  📄
                </div>
              )}
            </div>

            {/* File info */}
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', color: '#374151' }}>
                {selectedFile.name}
              </h4>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                {formatFileSize(selectedFile.size)}
              </div>

              {uploading ? (
                <div style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Subiendo...</span>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      backgroundColor: '#2563eb',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              ) : uploadProgress === 100 ? (
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #10b981',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#059669',
                  marginBottom: '0.5rem'
                }}>
                  ✅ Archivo subido exitosamente
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {uploadProgress < 100 && (
                  <button
                    type="button"
                    onClick={uploadFile}
                    disabled={uploading}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      backgroundColor: uploading ? '#9ca3af' : '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploading ? 'Subiendo...' : 'Subir'}
                  </button>
                )}

                <button
                  type="button"
                  onClick={removeFile}
                  disabled={uploading}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: uploading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Quitar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: '#fffbeb',
        border: '1px solid #f59e0b',
        borderRadius: '6px',
        fontSize: '0.875rem'
      }}>
        <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '0.5rem' }}>
          💡 Consejos para el comprobante:
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#92400e' }}>
          <li>Asegúrate de que la imagen esté clara y legible</li>
          <li>Incluye toda la información: fecha, hora, monto y destinatario</li>
          <li>El comprobante debe mostrar claramente la transferencia realizada</li>
        </ul>
      </div>
    </div>
  );
};

export default ReceiptUpload;